import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import { addNewRelicPageAction } from 'ddc-new-relic';
import { diff } from 'deep-object-diff';
import loadShift from '../utils/loadShift';

import DataMapper from '../components/DataMapper';
import EventViewer from '../components/EventViewer';
import LegacyTracking from '../components/LegacyTracking';

const SAME_EVENT_THRESHOLD = 1000;
const eventGroup = [];
let timeout;

const hasAnEventMappingConfiguration = (mapping) =>
	typeof Object.values(mapping).find(
		(val) => typeof val.event !== 'undefined'
	) !== 'undefined';

const eventViewerInitialState = (win) => {
	let show = false;

	if (new URL(win.location).searchParams.get('eventViewer') === 'false') {
		show = false;
	} else if (win.localStorage.getItem('eventViewer') === 'true') {
		show = true;
	} else if (
		new URL(win.location).searchParams.get('eventViewer') === 'true'
	) {
		show = true;
		window.localStorage.setItem('eventViewer', 'true');
	}

	return show;
};

export class WidgetContainer extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			eventViewer: false,
			eventViewerData: [],
			eventViewerCount: 0
		};
		this.handleEventViewerData = this.handleEventViewerData.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	componentDidMount() {
		this.setState(() => ({
			eventViewer: eventViewerInitialState(window)
		}));
		window.DDC = window.DDC || {};
		window.DDC.WidgetData = window.DDC.WidgetData || {};
		window.DDC.WidgetData['ws-tagging'] =
			window.DDC.WidgetData['ws-tagging'] || {};
		window.DDC.WidgetData['ws-tagging'].mapping = this.props.mapping;
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.publish('ws-tagging-ready');
		}

		window.addEventListener('eventViewerData', this.handleEventViewerData);

		window.addEventListener('keydown', this.handleKeydown);
	}

	componentWillUnmount() {
		window.removeEventListener(
			'eventViewerData',
			this.handleEventViewerData
		);
		window.removeEventListener('keydown', this.handleKeydown);
		clearTimeout(timeout); // added to pass a unit test but also makes sure setState doesn't run when component unmounted
	}

	handleEventViewerData({ detail: detailIn }) {
		const detail = {
			...detailIn?.common,
			...detailIn?.custom
		};

		eventGroup.push(detail);

		timeout = setTimeout(() => {
			this.setState(({ eventViewerData, eventViewerCount }) => {
				const newEventViewerData = JSON.parse(
					JSON.stringify(eventViewerData)
				);

				let newEventViewerCount = eventViewerCount;

				if (
					(this.props?.flags?.['ws-tagging-preview-enabled'] &&
						Object.keys(
							this.props.flags['ws-tagging-preview-enabled']
						).length) ||
					this.props?.flags?.['ws-tagging-adobe-preview-temp']
				) {
					detail.classes = 'same-unset';

					// if more than one thing comes through in the number of ms specified in SAME_EVENT_THRESHOLD
					// loop through them and determine if any are the same payload
					// this section designed to support QA of the legacy tracking solution
					if (eventGroup.length > 1) {
						let previousProperties;
						let same = 'same-true';
						eventGroup.forEach((event) => {
							const {
								classes: _classes,
								source: _source,
								...propertiesToMatch
							} = event;

							// For Ford as this is a random thing
							if (propertiesToMatch?.payload?.user?.cksVisitId) {
								propertiesToMatch.payload.user.cksVisitId =
									'[RANDOM]';
							}

							// for some clients like forddirect there is a property nrEvent we can match on to know it's
							// the same event
							const hasNrEventAndItMatches =
								previousProperties?.nrEvent ===
								propertiesToMatch?.nrEvent;

							if (
								previousProperties &&
								previousProperties.eventHandler ===
									propertiesToMatch.eventHandler &&
								hasNrEventAndItMatches &&
								JSON.stringify(previousProperties) !==
									JSON.stringify(propertiesToMatch)
							) {
								same = 'same-false';
								detail.diff = diff(
									previousProperties,
									propertiesToMatch
								);
								addNewRelicPageAction('wsTaggingLegacyError', {
									error: 'payload mismatch',
									payloadDiff: JSON.stringify(detail.diff),
									eventName: event.event,
									payloadOne: previousProperties,
									payloadTwo: propertiesToMatch,
									...(event?.eventHandler && {
										eventHandler: event.eventHandler
									})
								});
							}
							previousProperties = propertiesToMatch;
						});

						detail.classes = same;
					}
				}

				if (detail) {
					newEventViewerCount += 1;
					newEventViewerData.unshift({
						detail,
						eventViewerCount: newEventViewerCount
					});
				}

				eventGroup.length = 0;

				return {
					eventViewerData: newEventViewerData,
					eventViewerCount: newEventViewerCount
				};
			});
		}, SAME_EVENT_THRESHOLD);
	}

	handleKeydown({ altKey, ctrlKey, metaKey, shiftKey, key }) {
		if (
			altKey &&
			ctrlKey &&
			metaKey &&
			shiftKey &&
			['∆', 'Î'].includes(key)
		) {
			this.setState(
				({ eventViewer }) => ({
					eventViewer: !eventViewer
				}),
				() => {
					window.localStorage.setItem(
						'eventViewer',
						this.state.eventViewer
					);
				}
			);
		}
	}

	clearEventViewer = () => {
		this.setState(() => ({
			eventViewerCount: 0,
			eventViewerData: []
		}));
	};

	render() {
		loadShift(this.props.legacyConfigurations);

		return (
			<React.Fragment>
				{hasAnEventMappingConfiguration(this.props.mapping) && (
					<DataMapper mapping={this.props.mapping} />
				)}
				{this.state.eventViewer && (
					<EventViewer
						data={this.state.eventViewerData}
						clearEventViewer={this.clearEventViewer}
					/>
				)}
				{this.props.legacyConfigurations?.length > 0 && (
					<LegacyTracking
						legacyConfigurations={this.props.legacyConfigurations}
					/>
				)}
			</React.Fragment>
		);
	}
}

WidgetContainer.propTypes = {
	legacyConfigurations: PropTypes.objectOf(PropTypes.object),
	mapping: PropTypes.objectOf(PropTypes.object),
	flags: PropTypes.objectOf(PropTypes.object)
};

const mapStateToProps = (state) => {
	return {
		mapping: state.mapping,
		legacyConfigurations: state.legacyConfigurations,
		flags: state.flags
	};
};

const mapDispatchToProps = (_dispatch) => ({});

const Widget = connect(mapStateToProps, mapDispatchToProps)(WidgetContainer);

export default Widget;
